<script setup lang="ts">
import { computed, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { integrationsModule } from '@/store/integrations/module'
import { toastModule } from '@/store/toast/module'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'

type SelectEntry = {
    label: string
    value: string
}

const {
    SidePanel,
    Button: PocketButton,
    Select: PocketSelect,
    Input: PocketInput,
    Icon,
} = UIKit

const props = withDefaults(
    defineProps<{
        clientId?: string
        courseIntegrationId?: string
        orgExamId?: string
        instructors?: string[]
        group?: SelectEntry
    }>(),
    {
        clientId: '',
        courseIntegrationId: '',
        orgExamId: '',
        instructors: () => [],
        group: () => ({} as SelectEntry),
    }
)

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentCourseIntegrations = computed(() => {
    return integrationsModule.getters.getCurrentCourseIntegrations()
})

const currentCourseIntegration = computed(() => {
    return currentCourseIntegrations.value?.find(cci => cci.objectId === props.courseIntegrationId)
})

const mappedInstructors = computed(() => {
    return props.instructors.reduce((acc, i) => {
        const instructor = currentOrgInstructors.value?.[i]
        if (!instructor) {
            return acc
        }
        const instructorUser = currentOrgInstructorUsers.value?.[instructor.user.objectId]
        if (!instructorUser) {
            return acc
        }
        const firstString = instructorUser.firstName || instructorUser.email
        const lastName = instructorUser.lastName
        const instructorString = lastName ? `${firstString} ${lastName}` : firstString
        acc.push({ label: instructorString, value: i })
        return acc
    }, [] as SelectEntry[])
})

const deploymentId = computed(() => {
    return currentCourseIntegration.value?.deploymentId || ''
})

const courseName = computed(() => {
    return currentCourseIntegration.value?.name || ''
})

const exam = computed(() => {
    return currentOrgExams.value[props.orgExamId]?.displayName ?? ''
})

const emitClose = () => {
    emit('close', true)
}

const unarchiveIntegration = async () => {
    const current = currentCourseIntegration.value
    if (current) {
        await integrationsModule.actions.toggleLMSCourseIntegration({
            lmsCourseIntegrationId: current.objectId,
        })

        await integrationsModule.actions.fetchLMSCourseIntegrations(true)

        toastModule.actions.displayToast({
            title: 'Canvas course integration unarchived.',
        })

        emitClose()
    }
}

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        integrationsModule.actions.fetchLMSIntegrations(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
    ])
    await integrationsModule.actions.fetchLMSCourseIntegrations(true)
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            class="unarchive-integration"
            name="Archived Course Integration"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <div class="unarchive-integration__client-id-container">
                <span class="unarchive-integration__client-id-label">
                    Client ID:
                </span>
                {{ props.clientId }}
            </div>
            <PocketInput
                :modelValue="deploymentId"
                class="unarchive-integration__deployment-id"
                field-type="text"
                label="Deployment ID"
                :disabled="true"
            />
            <PocketInput
                :modelValue="courseName"
                class="unarchive-integration__course-name"
                field-type="text"
                label="Course Name"
                :disabled="true"
            />
            <div class="unarchive-integration__course-details-container">
                <label class="unarchive-integration__course-details">
                    Course Details
                </label>
                <p class="unarchive-integration__course-details-subtext">
                    These details will automatically be associated with any students who click the SSO link on Canvas.
                </p>
            </div>
            <div class="unarchive-integration__selects">
                <PocketInput
                    :modelValue="exam"
                    class="unarchive-integration__exam"
                    field-type="text"
                    label="Exam"
                    :disabled="true"
                />
                <PocketInput
                    :modelValue="props.group?.label"
                    class="unarchive-integration__group"
                    label="Group (optional)"
                    :disabled="true"
                />
                <div
                    v-if="exam && mappedInstructors"
                    class="unarchive-integration__instructors"
                >
                    <div class="unarchive-integration__instructors-label">
                        Instructors (optional)
                    </div>
                    <div
                        v-for="(option, index) in mappedInstructors"
                        :key="option.value"
                        class="unarchive-integration__instructor-row"
                    >
                        <PocketSelect
                            :modelValue="mappedInstructors[index]"
                            class="unarchive-integration__select-instructor"
                            :disabled="true"
                            :data="mappedInstructors"
                        />
                        <div
                            class="unarchive-integration__instructor-row-remove"
                        >
                            <Icon type="close" />
                        </div>
                        <div
                            v-if="mappedInstructors[index]?.value && index === mappedInstructors.length - 1"
                            class="unarchive-integration__instructor-row-add"
                        >
                            <Icon type="addCircle" />
                        </div>
                    </div>
                </div>
            </div>
            <template #action>
                <PocketButton
                    class="unarchive-integration__unarchive-btn"
                    @click="unarchiveIntegration"
                >
                    Unarchive Integration
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.unarchive-integration {
    :deep(*) {
        .uikit-side-panel__content {
            padding: 36px 32px;
        }
        .uikit-tooltip {
            position: absolute;
            top: -30px;
            left: 22px;
        }

        .banner__text {
            margin: 0;
        }
    }

    &__course-name,
    &__deployment-id {
        margin-top: 24px;
    }

    &__error-banner {
        margin-top: 12px;
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin: 0 0 4px 12px;
        color: $pickled-bluewood;
        display: block;
    }

    &__client-id-container {
        font-size: 13px;
        line-height: 20px;
    }

    &__client-id-label {
        font-weight: 600;
    }

    &__course-details-container {
        margin-top: 60px;
    }

    &__course-details {
        font-weight: 600;
    }

    &__course-details-subtext {
        line-height: 20px;
        margin: 7px 8px 0 0;
    }

    &__banner {
        margin-top: 24px;
        padding: 12px 24px 12px 16px;
        font-size: 15px;
    }

    &__exam,
    &__instructor,
    &__group {
        margin-top: 24px;
    }

    &__select-instructor {
        width: 433px;
        margin: 0;
    }

    &__instructors-label {
        margin: 27px 0 -19px 12px;
        font-size: 13px;
        font-weight: 600;
        color: $pickled-bluewood;
    }

    &__instructor-row {
        height: 36px;
        display: flex;
        align-items: center;
        margin-top: 23px;
    }

    &__instructor-row-add,
    &__instructor-row-remove {
        position: relative;
        padding: 4px 0 0 12px;
        color: $steel;
    }
}
</style>
